import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, CircularProgress } from '@mui/material';

const OpenHandler: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  useEffect(() => {
    // Check if we're handling a duplicate launch
    const lastLaunch = sessionStorage.getItem('itcp-last-launch');
    const now = Date.now();
    
    if (lastLaunch) {
      const timeSince = now - parseInt(lastLaunch);
      if (timeSince < 2000) {
        // Duplicate launch within 2 seconds, close this window
        console.log('Duplicate launch detected, closing...');
        window.close();
        return;
      }
    }
    
    // Store this launch time
    sessionStorage.setItem('itcp-last-launch', now.toString());
    
    // Get the URL parameter from the protocol handler
    const encodedUrl = searchParams.get('url');
    
    if (encodedUrl) {
      try {
        // The URL comes in as web+itcp://path/to/resource
        // We need to extract just the path
        const decodedUrl = decodeURIComponent(encodedUrl);
        
        // Clean up the protocol URL
        let path = decodedUrl;
        path = path.replace(/^web\+itcp:\/\//, '/');  // Remove web+itcp://
        path = path.replace(/^web\+itcp:/, '/');      // Remove web+itcp:
        
        // Ensure we have a valid path
        if (!path || path === '/' || path === '') {
          navigate('/dashboard');
          return;
        }
        
        // Ensure path starts with /
        if (!path.startsWith('/')) {
          path = '/' + path;
        }
        
        // Try to parse URL parameters if they exist
        try {
          const url = new URL(path, window.location.origin);
          const action = url.searchParams.get('action');
          const data = url.searchParams.get('data');
          
          // Handle different actions
          if (action === 'ticket') {
            navigate('/tickets/new', { state: { data } });
          } else if (action === 'chat') {
            navigate('/chat', { state: { userId: data } });
          } else if (action === 'meeting') {
            navigate('/meetings', { state: { meetingId: data } });
          } else {
            // Default navigation
            navigate(url.pathname + url.search);
          }
        } catch (urlError) {
          // If URL parsing fails, just navigate to the path
          console.log('URL parsing failed, navigating to:', path);
          navigate(path);
        }
      } catch (error) {
        console.error('Error handling protocol URL:', error);
        // On any error, go to dashboard
        navigate('/dashboard');
      }
    } else {
      // No URL provided, go to dashboard
      navigate('/dashboard');
    }
  }, [navigate, searchParams]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: theme => theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
          : 'linear-gradient(135deg, #1a5c2e 0%, #289546 100%)',
      }}
    >
      <CircularProgress size={48} sx={{ color: 'white', mb: 2 }} />
      <Typography variant="h6" sx={{ color: 'white' }}>
        Opening IT Control Panel...
      </Typography>
    </Box>
  );
};

export default OpenHandler;